import { WhiteboardState } from '../types';

export interface TreeExportData {
  version: string;
  exportDate: string;
  canvasName?: string;
  canvas: {
    scale: number;
    offset: { x: number; y: number };
  };
  notes: Array<{
    id: string;
    content: string;
    position: { x: number; y: number };
    color: string;
    zIndex: number;
    width: number;
    height: number;
    parentId?: string;
    level?: number;
    topic?: string;
    status?: string;
    showConverge?: boolean; // Controls if merge arrow is shown
    showConvergeGlow?: boolean; // Temporary glow effect for new converge lines
    mergeTargetId?: string; // ID of the note this branch was merged into
    chatHistory?: Array<{ sender: 'user' | 'ai'; text: string }>;
    mergeMessages?: Array<{
      messageIndex: number;
      sourceBranchId: string;
      sourceBranchName: string;
    }>;
    copyMessages?: Array<{
      messageIndex: number;
      sourceNoteId: string;
      sourceNoteName: string;
    }>;
    systemPrompt?: string; // Original detailed system prompt
    persona?: string; // Extracted persona/role title
    hasCustomSystemPrompt?: boolean;
    firstMessageSent?: boolean;
    needsPersonaSelection?: boolean;
    inheritedSystemPrompt?: string; // Original system prompt inherited from parent
    attachedFiles?: Array<{
      id: string;
      name: string;
      fileId?: string;
      size: number;
      type: string;
      uploadDate: string; // ISO string for serialization
      uploadStatus: 'uploading' | 'completed' | 'failed';
      url?: string;
      error?: string;
    }>;
  }>;
  branchRelationships: Array<{
    parentId: string;
    branchPoint: number;
    branchId: string;
    inheritanceType: string;
    createdAt: string;
  }>;
}

export interface ExportOptions {
  forComparison?: boolean;
}

export function exportTreeToYAML(state: WhiteboardState, options: ExportOptions = {}): string {
  const exportData: TreeExportData = {
    version: '1.0.0',
    exportDate: options.forComparison ? 'OMITTED_FOR_COMPARISON' : new Date().toISOString(),
    canvasName: state.canvasName,
    canvas: {
      scale: state.scale,
      offset: state.offset,
    },
    notes: state.notes.map(note => ({
      id: note.id,
      content: note.content,
      position: note.position,
      color: note.color,
      zIndex: note.zIndex,
      width: note.width,
      height: note.height,
      parentId: note.parentId,
      level: note.level,
      topic: note.topic,
      status: note.status,
      showConverge: note.showConverge,
      showConvergeGlow: note.showConvergeGlow,
      mergeTargetId: note.mergeTargetId,
      chatHistory: note.chatHistory,
      mergeMessages: note.mergeMessages,
      copyMessages: note.copyMessages,
      systemPrompt: note.systemPrompt,
      persona: note.persona,
      hasCustomSystemPrompt: note.hasCustomSystemPrompt,
      firstMessageSent: note.firstMessageSent,
      needsPersonaSelection: note.needsPersonaSelection,
      inheritedSystemPrompt: note.inheritedSystemPrompt,
      attachedFiles: note.attachedFiles?.map(file => ({
        id: file.id,
        name: file.name,
        fileId: file.fileId,
        size: file.size,
        type: file.type,
        uploadDate: file.uploadDate.toISOString(),
        uploadStatus: file.uploadStatus,
        url: file.url,
        error: file.error,
      })),
    })),
    branchRelationships: state.branchRelationships.map(rel => ({
      parentId: rel.parentId,
      branchPoint: rel.branchPoint,
      branchId: rel.branchId,
      inheritanceType: rel.inheritanceType,
      createdAt: rel.createdAt.toISOString(),
    })),
  };

  // Convert to YAML format
  const yaml = convertToYAML(exportData);
  return yaml;
}

function convertToYAML(obj: any, indent: number = 0): string {
  const spaces = '  '.repeat(indent);
  let yaml = '';

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return spaces + '[]\n';
    }
    for (const item of obj) {
      if (typeof item === 'object' && item !== null) {
        yaml += spaces + '-\n' + convertToYAML(item, indent + 1);
      } else {
        yaml += spaces + '- ' + formatValue(item, spaces + '  ') + '\n';
      }
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined || value === null) {
        continue;
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        yaml += spaces + key + ':\n' + convertToYAML(value, indent + 1);
      } else if (Array.isArray(value)) {
        // Handle empty arrays inline, non-empty arrays as blocks
        if (Array.isArray(value) && value.length === 0) {
          yaml += spaces + key + ': []\n';
        } else {
          yaml += spaces + key + ':\n' + convertToYAML(value, indent + 1);
        }
      } else {
        yaml += spaces + key + ': ' + formatValue(value, spaces + '  ') + '\n';
      }
    }
  }

  return yaml;
}

function formatValue(value: any, indentSpaces: string = ''): string {
  if (typeof value === 'string') {
    // Quote hex color values to prevent YAML comment interpretation
    if (value.startsWith('#')) {
      return '"' + value + '"';
    }
    // Use YAML literal block scalar format for multi-line strings to preserve newlines
    if (value.includes('\n')) {
      const lines = value.split('\n');
      if (lines.length > 1) {
        return '|\n' + indentSpaces + lines.join('\n' + indentSpaces);
      }
    }
    // Escape special characters and wrap in quotes if needed
    if (value.includes('"') || value.includes("'") || value.includes(':')) {
      return '"' + value.replace(/"/g, '\\"') + '"';
    }
    return value;
  } else if (typeof value === 'number') {
    return value.toString();
  } else if (typeof value === 'boolean') {
    return value.toString();
  } else if (value === null) {
    return 'null';
  }
  return String(value);
}

export function exportTreeToJSON(state: WhiteboardState, options: ExportOptions = {}): string {
  const exportData: TreeExportData = {
    version: '1.0.0',
    exportDate: options.forComparison ? 'OMITTED_FOR_COMPARISON' : new Date().toISOString(),
    canvasName: state.canvasName,
    canvas: {
      scale: state.scale,
      offset: state.offset,
    },
    notes: state.notes.map(note => ({
      id: note.id,
      content: note.content,
      position: note.position,
      color: note.color,
      zIndex: note.zIndex,
      width: note.width,
      height: note.height,
      parentId: note.parentId,
      level: note.level,
      topic: note.topic,
      status: note.status,
      showConverge: note.showConverge,
      showConvergeGlow: note.showConvergeGlow,
      mergeTargetId: note.mergeTargetId,
      chatHistory: note.chatHistory,
      mergeMessages: note.mergeMessages,
      copyMessages: note.copyMessages,
      systemPrompt: note.systemPrompt,
      persona: note.persona,
      hasCustomSystemPrompt: note.hasCustomSystemPrompt,
      firstMessageSent: note.firstMessageSent,
      needsPersonaSelection: note.needsPersonaSelection,
      inheritedSystemPrompt: note.inheritedSystemPrompt,
      attachedFiles: note.attachedFiles?.map(file => ({
        id: file.id,
        name: file.name,
        fileId: file.fileId,
        size: file.size,
        type: file.type,
        uploadDate: file.uploadDate.toISOString(),
        uploadStatus: file.uploadStatus,
        url: file.url,
        error: file.error,
      })),
    })),
    branchRelationships: state.branchRelationships.map(rel => ({
      parentId: rel.parentId,
      branchPoint: rel.branchPoint,
      branchId: rel.branchId,
      inheritanceType: rel.inheritanceType,
      createdAt: rel.createdAt.toISOString(),
    })),
  };

  return JSON.stringify(exportData, null, 2); // Pretty-printed JSON
}

export function downloadJSON(jsonContent: string, filename: string): void {
  const blob = new Blob([jsonContent], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadYAML(yamlContent: string, filename: string): void {
  const blob = new Blob([yamlContent], { type: 'text/yaml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.yaml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
} 